import { inject, Injectable, Provider } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  Route,
  RouteReuseStrategy,
} from '@angular/router'

import { DeviceService } from '../services/device.service'
import { whenMainThreadSettles } from '../utils/when-main-thread-settles'

// About 3 s at most, with the idle callback that follows the wait
const DESTROY_DELAY = { quietMs: 500, maxWaitMs: 2000, fallbackMs: 1000 }

@Injectable()
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  protected readonly cache = new Map<
    Route,
    {
      handle: DetachedRouteHandle
      route: ActivatedRouteSnapshot
      timestamp: number
    }
  >()

  /** Evicted handles waiting for the main thread to settle before they are destroyed */
  protected readonly pendingDestroy = new Map<
    Route,
    { handle: DetachedRouteHandle; route: ActivatedRouteSnapshot }
  >()

  protected readonly device = inject(DeviceService)
  protected readonly MAX_CACHED_PAGES = this.getMaxCachedPages()

  private isCleanupScheduled = false

  public shouldDetach(route: ActivatedRouteSnapshot): boolean {
    // This ensures all disposable popups are closed after route is changed
    setTimeout(() => document.body.click(), 128)

    // A route opts out of reuse with `data: { reuseRoute: false }`. Parameterized detail/edit
    // screens rebuild themselves from their route params, so serving a cached view means
    // serving state that belongs to a previously opened entity.
    return !!route && route.routeConfig?.data?.['reuseRoute'] !== false
  }

  /**
   * The router calls `store(route, null)` right after a stored handle is attached, to release
   * its ownership of that handle. Such an entry must be removed instead of being kept with an
   * empty handle: `shouldAttach()` would keep reporting a cached route while `retrieve()`
   * returns `null`, and the router dereferences the retrieved handle without a null check.
   */
  public store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!route.routeConfig) return

    if (!handle) {
      this.cache.delete(route.routeConfig)
      return
    }

    this.cache.set(route.routeConfig, { handle, route, timestamp: Date.now() })
    // The router stores a page's child routes before the page, so pages are counted once all are in
    if (!this.isCleanupScheduled) {
      this.isCleanupScheduled = true
      queueMicrotask(() => {
        this.isCleanupScheduled = false
        this.cleanupCachedRoutes()
      })
    }
  }

  public shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!route.routeConfig) return false
    this.restorePending(route.routeConfig)
    return !!this.cache.get(route.routeConfig)?.handle
  }

  public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!route.routeConfig || !this.cache.has(route.routeConfig)) {
      return null
    }
    return this.cache.get(route.routeConfig)?.handle || null
  }

  public shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig
  }

  protected getMaxCachedPages() {
    if (!this.device.isMobilePlatform) return 8

    // Rounded down to a power of two and capped at 8, so 8 means at least 8 GB; Safari reports nothing
    const memoryGb = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 0
    // Cached pages keep their DOM and map WebGL contexts, and a renderer killed for memory reloads the app
    return memoryGb >= 8 ? 5 : 3
  }

  /**
   * Evicts the least recently left pages beyond the limit. Every route level with a component is
   * stored on its own, so a page is a cached route without a cached ancestor, and its cached child
   * routes are evicted with it: evicting a child alone rebuilt the page's content on return.
   */
  protected cleanupCachedRoutes() {
    const isCached = (snapshot: ActivatedRouteSnapshot) =>
      !!snapshot.routeConfig && this.cache.has(snapshot.routeConfig)
    const pages = Array.from(this.cache.entries())
      .filter(([, { route }]) => !route.pathFromRoot.slice(0, -1).some(isCached))
      .sort(([, a], [, b]) => a.timestamp - b.timestamp)

    for (const [pageConfig] of pages.slice(0, Math.max(0, pages.length - this.MAX_CACHED_PAGES))) {
      for (const [routeConfig, { route, handle }] of Array.from(this.cache.entries())) {
        if (route.pathFromRoot.some((snapshot) => snapshot.routeConfig === pageConfig)) {
          this.cache.delete(routeConfig)
          this.scheduleDestroy(routeConfig, handle, route)
        }
      }
    }
  }

  // Destroying a page with a map takes 100–200 ms, too long for the navigation that evicts it
  protected scheduleDestroy(
    routeConfig: Route,
    handle: DetachedRouteHandle,
    route: ActivatedRouteSnapshot,
  ) {
    this.pendingDestroy.set(routeConfig, { handle, route })
    whenMainThreadSettles(DESTROY_DELAY).then(() => {
      if (this.pendingDestroy.get(routeConfig)?.handle === handle) {
        this.destroyPending(routeConfig)
      }
    })
  }

  /** An evicted page reopened before it is destroyed is reused, with its evicted child routes */
  protected restorePending(pageConfig: Route) {
    if (!this.pendingDestroy.has(pageConfig)) return

    for (const [routeConfig, entry] of Array.from(this.pendingDestroy.entries())) {
      if (entry.route.pathFromRoot.some((snapshot) => snapshot.routeConfig === pageConfig)) {
        this.pendingDestroy.delete(routeConfig)
        this.cache.set(routeConfig, { ...entry, timestamp: Date.now() })
      }
    }
  }

  protected destroyPending(routeConfig: Route) {
    const handle = this.pendingDestroy.get(routeConfig)?.handle
    if (!handle) return
    this.pendingDestroy.delete(routeConfig)

    // Properly destroy the cached handle so ngOnDestroy is triggered.
    // Only detached handles are cached, so an attached route can never be destroyed here.
    try {
      const anyHandle = handle as any
      // Angular stores the componentRef on the handle in most implementations
      const componentRef =
        anyHandle?.componentRef ?? anyHandle?.route?.componentRef ?? anyHandle?.['componentRef']

      // If the handle exposes a detach() or similar, call it first (framework dependent)
      if (typeof anyHandle?.destroy === 'function') {
        anyHandle.destroy()
      } else if (componentRef) {
        // Detach from app and destroy to trigger ngOnDestroy in the whole tree
        componentRef.hostView?.detach?.()
        componentRef.destroy()
      }
    } catch (e) {
      console.warn(`Cannot destroy cached component`, e)
    }
  }
}

export const provideCustomRouteReuseStrategy = (): Provider[] => {
  return [{ provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy }]
}
